import ComponentNotifier from '../../services/wss/ComponentNotifier';
import { publish } from '../../services/utils/pubsub';
import { JxWindowEvents } from '../JxWindowManager/JxWindowEvents';
import jxWindowManager from '../JxWindowManager/JxWindowManager';
import jxWindowEngine from '../JxWindowManager/JxWindowEngine';

import JxFormValidationBase from './JxFormValidationBase';

// Pinned windows always render above non-pinned windows, regardless of
// selection order
const PINNED_OFFSET = 10000;

/**
 * JxFormBase — window management shell for all JxForm dialogs.
 */
export default class JxFormBase extends JxFormValidationBase {
    state = {
        ...this.state,
        zIndex: 1500,
        isActive: false,
        isPinned: false,
        // single geo member for the whole window chain (draggable, resizable, or fixed) —
        // placeholder until componentDidMount resolves it to cache-or-default
        geo: { top: 0, left: 0, width: 0, height: 0 },
    };

    _unsubscribeSelectWindow = null;
    // last raw SelectWindow payload relevant to this window — not React state,
    // purely a recompute cache for togglePin(). See class doc above.
    _lastSelectEvent = null; // { zIndex, isSelf }

    componentDidMount() {
        super.componentDidMount();

        this._unsubscribeSelectWindow = ComponentNotifier.subscribe('JxWindowEngine', (e) => {
            try {
                this._lastSelectEvent = {
                    zIndex: e.data.zIndex,
                    isSelf: e.data?.id === this.getId(),
                };
                this._applyZIndex();
            } catch (error) {
                this.logError('componentDidMount.onSelectWindow', error.message);
            }
        });

        // geo resolution — cache wins entirely, or default wins entirely, never merged/computed.
        const cached = jxWindowEngine.getPos(this.getId());
        this.setState({ geo: cached ?? this.getDefaultGeo() });
    }

    componentWillUnmount() {
        if (this._unsubscribeSelectWindow) this._unsubscribeSelectWindow();
        super.componentWillUnmount();
    }

    // ─── active state ─────────────────────────────────────────

    get panelActiveClass() {
        return this.state.isActive ? 'jx-panel--active' : '';
    }

    /**
     * Brings this window to the top. No-op if already active.
     * Public alias: setActive().
     */
    bringToTop = (e) => {
        try {
            e?.stopPropagation();
            if (this.state.isActive) return;
            publish(JxWindowEvents.channel, {
                name: JxWindowEvents.onWindowSelected,
                target: this.getId(),
                data: null,
            });
        } catch (error) {
            this.logError('bringToTop', error.message);
        }
    };

    setActive = () => this.bringToTop();

    isActive = () => this.state.isActive;

    // ─── pinned state ─────────────────────────────────────────

    get panelPinnedClass() {
        return this.state.isPinned ? 'jx-panel--pinned' : '';
    }

    /**
     * Applies PINNED_OFFSET (if pinned) on top of the last known raw zIndex.
     * Called after every SelectWindow broadcast, and again from togglePin() so a
     * pin/unpin takes effect immediately instead of waiting for the next unrelated
     * selection elsewhere. Non-selected windows keep their current zIndex — only
     * isActive is flipped to false, their stacking position is never touched here.
     */
    _applyZIndex = () => {
        try {
            if (!this._lastSelectEvent) return;

            const { zIndex, isSelf } = this._lastSelectEvent;
            const offset = this.state.isPinned ? PINNED_OFFSET : 0;

            if (isSelf) {
                this.setState({ zIndex: zIndex + offset, isActive: true });
            } else {
                this.setState({ isActive: false });
            }
        } catch (error) {
            this.logError('_applyZIndex', error.message);
        }
    };

    /**
     * Flips isPinned and immediately recomputes zIndex via _applyZIndex —
     * covers both directions: pin-on while inactive (offset applies right
     * away, no need to wait for a click) and unpin while active (offset
     * drops immediately instead of lingering until another window is selected).
     * Also brings the window to top if it wasn't already active.
     */
    togglePin = () => {
        try {
            this.setState(
                (prev) => ({ isPinned: !prev.isPinned }),
                () => {
                    this._applyZIndex();
                    if (!this.state.isActive) this.bringToTop();
                },
            );
        } catch (error) {
            this.logError('togglePin', error.message);
        }
    };

    isPinned = () => this.state.isPinned;

    // ─── geo ──────────────────────────────────────────────────

    /**
     * Every window (draggable, resizable, or fixed) MUST override this with a literal,
     * hardcoded geo — no computed centering, no window.innerWidth math, no fallback chain.
     * Used only when jxWindowEngine.getPos(this.getId()) returns null (never cached before).
     * @returns {{top:number,left:number,width:number,height:number}}
     */
    getDefaultGeo() {
        this.logError('getDefaultGeo', `not overridden by ${this.name} — every window must define its own literal default geo`);
        return { top: 0, left: 0, width: 400, height: 300 };
    }

    /**
     * Returns current geo from state.
     * @returns {{top:number,left:number,width:number,height:number}}
     */
    getWindowGeo = () => this.state.geo;

    /**
     * Replaces geo in state — full replace, no partial merge.
     * @param {{top:number,left:number,width:number,height:number}} geo
     */
    setWindowGeo = (geo) => {
        this.setState({ geo });
    };

    // ─── title ────────────────────────────────────────────────

    /** @returns {string|undefined} */
    getTitle = () => this.props.title;

    /**
     * Updates the title via JxWindowManager — triggers repaint.
     * @param {string} title
     */
    setTitle = (title) => {
        try {
            jxWindowManager.updateWindowProps(this.getId(), { title });
        } catch (error) {
            this.logError('setTitle', error.message);
        }
    };
}
